import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ResponsiveTable } from "../../components/dash/ui";
import { formatRupiah, formatDay, isManagerRole } from "../../lib/labels";
import { uploadFile } from "../../lib/field";
import { Section, ProofLink, COST_LABELS, today } from "./shared";
import { DateField } from "./DateField";
import { Label } from "../../components/ui/label";

const Field = ({ label, className = "", children }) => <div className={`space-y-1 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;

const selectCls = "flex h-10 w-full rounded-md border border-input bg-white px-3 text-sm";

const CostForm = ({ cid, onDone }) => {
  const [f, setF] = useState({ costDate: today(), category: "PEMELIHARAAN", description: "", amount: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (file) await uploadFile(`/profit/contracts/${cid}/costs`, file, f);
      else { const fd = new FormData(); Object.entries(f).forEach(([k, v]) => fd.append(k, v)); await api.post(`/profit/contracts/${cid}/costs`, fd); }
      toast.success("Biaya tercatat");
      setF((s) => ({ ...s, description: "", amount: "" })); setFile(null); e.target.reset(); onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-5 items-end" data-testid="cost-form">
      <Field label="Tanggal biaya *"><DateField value={f.costDate} onChange={(v) => setF((s) => ({ ...s, costDate: v }))} testid="cost-date" /></Field>
      <Field label="Kategori *"><select value={f.category} onChange={set("category")} className={selectCls} data-testid="cost-category">
        {Object.entries(COST_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select></Field>
      <Field label="Keterangan *" className="lg:col-span-2"><Input required placeholder="mis. upah penyiangan" value={f.description} onChange={set("description")} data-testid="cost-description" /></Field>
      <Field label="Jumlah (Rp) *"><Input type="number" inputMode="numeric" required min="1" value={f.amount} onChange={set("amount")} data-testid="cost-amount" /></Field>
      <Field label="Bukti (opsional)"><Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="cost-file" /></Field>
      <Button disabled={busy} className="sm:col-span-2 lg:col-span-6 bg-emerald-800 hover:bg-emerald-900" data-testid="cost-save-btn">Catat Biaya Operasional</Button>
    </form>
  );
};

export const CostsTab = ({ data, reload }) => {
  const { user } = useAuth();
  const c = data.contract;
  const remove = async (id) => {
    try { await api.delete(`/profit/costs/${id}`); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const saleCode = (sid) => { const s = data.sales.find((x) => x.id === sid); return s ? `Panen ${formatDay(s.harvestDate)}` : "-"; };
  const columns = [
    { key: "costDate", label: "Tanggal", render: (r) => formatDay(r.costDate) },
    { key: "category", label: "Kategori", render: (r) => COST_LABELS[r.category] || r.category },
    { key: "description", label: "Keterangan" },
    { key: "amount", label: "Jumlah", render: (r) => <span className="font-mono">{formatRupiah(r.amount)}</span> },
    { key: "saleId", label: "Status", render: (r) => r.saleId ? `Sudah dipotong (${saleCode(r.saleId)})` : "Belum dipotong" },
    { key: "proof", label: "Bukti", render: (r) => <ProofLink id={r.proofFileId} testid={`cost-proof-${r.id}`} /> },
  ];
  return (
    <Section title="Biaya Operasional Setelah Tanam" testid="costs-section">
      <p className="text-sm text-slate-600 mb-4">Hanya biaya operasional setelah tanam yang dipotong dari hasil penjualan. Modal RAB tidak dipotong lagi. Setiap biaya otomatis dipotong satu kali pada penjualan pertama yang tanggalnya sama atau setelah tanggal biaya.</p>
      {isManagerRole(user.role) && c.status === "LOCKED" && <CostForm cid={c.id} onDone={reload} />}
      {c.status !== "LOCKED" && <p className="text-sm text-amber-800 mb-4">Biaya bisa dicatat setelah skema LOCKED.</p>}
      <ResponsiveTable columns={columns} rows={data.costs} testid="costs-table" empty="Belum ada biaya operasional."
        actions={isManagerRole(user.role) ? (r, p) => !r.saleId && !r.carriedFromSaleId && <button onClick={() => remove(r.id)} className="text-red-700 p-1" aria-label="Hapus" data-testid={`${p}cost-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button> : undefined} />
    </Section>
  );
};
